
import { defineComponent, computed, ref, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useTelegramAlertsStore, GroupedAlert } from "@/stores/telegramAlerts";
import { useAlertPermissions } from "@/composables/useAlertPermissions";

// Components
import AlertsFilters from "@/components/alerts/AlertsFilters.vue";
import AlertsKPIs from "@/components/alerts/AlertsKPIs.vue";
import ForexAlertsTable from "@/components/alerts/tables/ForexAlertsTable.vue";
import SportsAlertsTable from "@/components/alerts/tables/SportsAlertsTable.vue";
import OrphanedMessages from "@/components/alerts/OrphanedMessages.vue";
import ManualLinkModal from "@/components/alerts/ManualLinkModal.vue";

export default defineComponent({
  name: "Alerts",
  components: {
    AlertsFilters,
    AlertsKPIs,
    ForexAlertsTable,
    SportsAlertsTable,
    OrphanedMessages,
    ManualLinkModal,
  },
  setup() {
    const store = useTelegramAlertsStore();
    const { canEditAlerts, canReanalyzeAlerts, canLinkMessages } =
      useAlertPermissions();

    // State
    const expandedAlertId = ref<Set<string>>(new Set());
    const isReprocessing = ref<Record<number, boolean>>({});
    const isLinking = ref(false);

    // Edit Mode State
    const editingAlertId = ref<string | null>(null);
    const isSaving = ref(false);

    // Manual Linking State
    const showLinkModal = ref(false);
    const linkTargetMessage = ref<any>(null);

    const {
      channels,
      selectedChannel,
      selectedChannelId,
      groupedAlerts,
      orphanedMessages,
      kpis,
      isLoading,
      filterYear,
      filterMonth,
    } = storeToRefs(store);

    // Computed
    const currentAlertType = computed(() => {
      return selectedChannel.value?.alertsType || "sports_betting";
    });

    const selectedYear = computed({
      get: () => filterYear.value,
      set: (val) => store.updateFilters(val, undefined),
    });

    const selectedMonth = computed({
      get: () => filterMonth.value,
      set: (val) => store.updateFilters(undefined, val),
    });

    // Date Utils
    const months = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];

    const years = computed(() => {
      const current = new Date().getFullYear();
      const list: number[] = [];
      for (let y = 2024; y <= current; y++) {
        list.push(y);
      }
      return list;
    });

    // Methods
    onMounted(() => {
      store.fetchChannels();
    });

    const selectChannel = (channelId: string) => {
      store.selectChannel(channelId);
    };

    const refreshAlerts = async () => {
      await store.fetchMessages();
    };

    const toggleExpand = (alertId: string) => {
      const newSet = new Set(expandedAlertId.value);
      if (newSet.has(alertId)) {
        newSet.delete(alertId); // Collapse if already expanded
      } else {
        newSet.add(alertId); // Expand without collapsing others
      }
      expandedAlertId.value = newSet;
    };

    // Editing Logic
    const startEditing = (alert: GroupedAlert) => {
      if (!canEditAlerts.value) {
        window.alert(
          "No tienes permisos para editar alertas. Se requiere el rol alertsManager."
        );
        return;
      }
      editingAlertId.value = alert.alertId;
    };

    const cancelEditing = () => {
      editingAlertId.value = null;
    };

    const saveEditing = async (payload: { alert: GroupedAlert; data: any }) => {
      if (!canEditAlerts.value) {
        window.alert(
          "No tienes permisos para editar alertas. Se requiere el rol alertsManager."
        );
        return;
      }

      const { alert, data } = payload;
      isSaving.value = true;
      try {
        await store.updateAlertManually(
          alert.mainMessage.channelId,
          alert.mainMessage.messageId,
          data
        );
        cancelEditing();
      } catch (error: any) {
        console.error("Error updating alert:", error);
        window.alert(
          "Error al actualizar la alerta: " +
            (error?.message || "Error desconocido")
        );
      } finally {
        isSaving.value = false;
      }
    };

    // Reprocessing Logic
    const handleReprocessMessage = async (message: any) => {
      if (!canReanalyzeAlerts.value) {
        window.alert(
          "No tienes permisos para reprocesar mensajes. Se requiere el rol alertsManager."
        );
        return;
      }

      isReprocessing.value[message.messageId] = true;
      try {
        await store.reprocessMessage(message.channelId, message.messageId);
      } catch (error: any) {
        console.error("Error reprocessing message:", error);
        alert(
          "Error al reprocesar el mensaje: " +
            (error.message || "Error desconocido")
        );
      } finally {
        isReprocessing.value[message.messageId] = false;
      }
    };

    // Linking Logic
    const openLinkModal = (message: any) => {
      if (!canLinkMessages.value) {
        window.alert(
          "No tienes permisos para vincular mensajes. Se requiere el rol alertsManager."
        );
        return;
      }

      linkTargetMessage.value = message;
      showLinkModal.value = true;
    };

    const closeLinkModal = () => {
      showLinkModal.value = false;
      linkTargetMessage.value = null;
    };

    const handleLinkMessage = async (payload: {
      targetAlertId: number;
      linkType: any;
    }) => {
      if (!canLinkMessages.value) {
        window.alert(
          "No tienes permisos para vincular mensajes. Se requiere el rol alertsManager."
        );
        return;
      }

      if (!linkTargetMessage.value) return;

      isLinking.value = true;
      try {
        await store.linkMessages(
          linkTargetMessage.value.channelId,
          payload.targetAlertId,
          linkTargetMessage.value.messageId,
          payload.linkType
        );
        closeLinkModal();
      } catch (error: any) {
        console.error("Error linking messages:", error);
        alert(
          "Error al vincular mensajes: " +
            (error.message || "Error desconocido")
        );
      } finally {
        isLinking.value = false;
      }
    };

    return {
      // State
      channels,
      selectedChannelId,
      groupedAlerts,
      orphanedMessages,
      kpis,
      isLoading,
      currentAlertType,
      selectedMonth,
      selectedYear,
      months,
      years,
      expandedAlertId,
      editingAlertId,
      isSaving,
      isReprocessing,
      isLinking,
      showLinkModal,
      linkTargetMessage,

      // Actions
      selectChannel,
      refreshAlerts,
      toggleExpand,
      startEditing,
      cancelEditing,
      saveEditing,
      handleReprocessMessage,
      openLinkModal,
      closeLinkModal,
      handleLinkMessage,
    };
  },
});
